// 261030 (acting fallback FE) — thẻ cờ «AI từ chối viết diễn xuất» ở tab Cần xem + hộp viết diễn xuất tay. Quyết định ở workflowFourActingBlockedUtils.ts (thuần, có test).
import React, { useEffect, useState } from 'react';
import { Button, Input, Modal, Tag } from 'antd';
import { filmApi } from '../../services/api/film';
import { apiErrorText } from './apiErrorText';
import {
  ACTING_MANUAL_LABEL, ACTING_MANUAL_FIELDS, actingManualDraftOf, actingManualPatchesOf, actingManualSaveTextOf,
  type ActingBlockedCard, type ActingManualDraft, type ActingManualKey,
} from './workflowFourActingBlockedUtils';

interface CardsProps {
  cards: ActingBlockedCard[];
  busy: boolean;
  /** eventId đang chạy «Thử lại bằng AI» (khoá nút của đúng video đó). */
  retryingEventId: string | null;
  onManual: (eventId: string) => void;
  onRetry: (card: ActingBlockedCard) => void;
}

/** Mỗi video một thẻ. Gợi ý — KHÔNG chặn duyệt. Hai nút: viết tay (mở hộp sửa) · thử lại bằng AI (qua hộp xác nhận ở trang). */
export const ActingBlockedCards: React.FC<CardsProps> = ({ cards, busy, retryingEventId, onManual, onRetry }) => (
  <>
    {cards.map((card) => (
      <article className="wf4-flag" data-testid={`flag-acting-blocked-${card.eventId ?? card.key}`} key={card.key}>
        <div>
          <b>{card.title}</b>
          <Tag style={{ marginLeft: 8 }}>Gợi ý — không chặn duyệt</Tag>
          {card.handWritten && <Tag color="green" style={{ marginLeft: 8 }}>đã có diễn xuất viết tay</Tag>}
          <p className="muted">Video vẫn đi tiếp được với câu trong truyện. Muốn diễn xuất sinh động hơn thì viết tay, hoặc nhờ AI thử lại.</p>
          {card.reasonLine && <p className="muted">{card.reasonLine}</p>}
          {card.retryWhy && <p className="muted" data-testid="acting-retry-why">{card.retryWhy}</p>}
        </div>
        <div className="wf4-flag-actions">
          {card.canManual && card.eventId && <Button size="small" data-testid="acting-blocked-manual" disabled={busy} onClick={() => onManual(card.eventId!)}>Viết diễn xuất tay</Button>}
          {card.eventId && <Button size="small" data-testid="acting-blocked-retry" disabled={!card.canRetry || busy} loading={retryingEventId === card.eventId} title={card.retryWhy ?? undefined} onClick={() => onRetry(card)}>Thử lại bằng AI</Button>}
        </div>
      </article>
    ))}
  </>
);

interface ManualProps {
  open: boolean;
  event: any | null;
  onClose: () => void;
  /** Gọi sau khi LƯU ĐƯỢC ít nhất một shot (trang tải lại trạng thái). */
  onSaved: () => void;
}

/** Hộp viết diễn xuất tay: ba ô chữ cho mỗi shot của video, lưu bằng đường sửa shot có sẵn (PUT event-shots/:shotId). Chỉ gửi ô đã đổi; BE tự đánh dấu «sửa tay» nên AI không ghi đè. */
export const ActingManualModal: React.FC<ManualProps> = ({ open, event, onClose, onSaved }) => {
  const shots: any[] = Array.isArray(event?.shots) ? event.shots.slice().sort((a: any, b: any) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0)) : [];
  const [draft, setDraft] = useState<ActingManualDraft>({});
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState<{ type: 'success' | 'warning'; text: string } | null>(null);
  // Nháp chỉ nạp lại khi MỞ hộp / đổi video — không nạp lại mỗi lần trang poll (sẽ xoá chữ đang gõ).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) { setDraft(actingManualDraftOf(shots)); setNote(null); } }, [open, event?.id]);
  const set = (shotId: string, key: ActingManualKey, value: string) => setDraft((old) => ({ ...old, [shotId]: { ...old[shotId], [key]: value } }));
  const patches = actingManualPatchesOf(shots, draft);
  const save = async () => {
    if (!patches.length) { setNote({ type: 'warning', text: actingManualSaveTextOf({ total: 0, saved: 0 }) }); return; }
    setSaving(true);
    let saved = 0;
    try {
      for (const p of patches) { await filmApi.updateEventShot(p.shotId, p.patch as Record<string, unknown>); saved += 1; }
      setNote({ type: 'success', text: actingManualSaveTextOf({ total: patches.length, saved }) });
      onSaved(); onClose();
    } catch (error) {
      const failed = shots.find((s) => s.id === patches[saved]?.shotId);
      setNote({ type: 'warning', text: actingManualSaveTextOf({ total: patches.length, saved, failedShotOrder: failed?.orderIndex ?? null, errorText: apiErrorText(error, 'Lưu diễn xuất') }) });
      if (saved > 0) onSaved();
    } finally { setSaving(false); }
  };
  return (
    <Modal open={open} width={720} title={event ? `Viết diễn xuất tay — Video ${event.eventOrder ?? ''}`.trim() : 'Viết diễn xuất tay'} okText="Lưu diễn xuất" cancelText="Huỷ" confirmLoading={saving} okButtonProps={{ disabled: !patches.length }} onCancel={() => { if (!saving) onClose(); }} onOk={() => void save()} destroyOnHidden>
      <p className="muted">Mỗi shot đang mang câu lấy nguyên từ truyện. Viết lại cho sinh động (nét mặt, cơ thể, giọng). Lưu xong máy không ghi đè phần bạn đã viết; không tốn tiền AI.</p>
      {shots.length ? shots.map((shot) => (
        <div className="wf4-shot" key={shot.id} data-testid={`acting-manual-shot-${shot.id}`}>
          <b>Shot {shot.orderIndex}</b>
          {ACTING_MANUAL_FIELDS.map((key) => (
            <div key={key} style={{ marginTop: 6 }}>
              <label className="muted" htmlFor={`am-${shot.id}-${key}`}>{ACTING_MANUAL_LABEL[key]}</label>
              <Input.TextArea id={`am-${shot.id}-${key}`} rows={key === 'performance' ? 3 : 1} value={draft[shot.id]?.[key] ?? ''} onChange={(e) => set(shot.id, key, e.target.value)} />
            </div>
          ))}
        </div>
      )) : <p className="muted">Video này chưa có shot.</p>}
      {note && <p role="status" style={{ marginTop: 10, color: note.type === 'warning' ? 'var(--warn)' : 'var(--ok, #389e0d)' }} data-testid="acting-manual-note">{note.text}</p>}
    </Modal>
  );
};
